import { FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';

import { BottomSheet } from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import {
  BuildingSelect,
  BuildingSelectItem,
  toBuilding,
  toBuildingSelectItem,
} from 'src/components/BuildingSelect/BuildingSelect';
import {
  FloorSelect,
  FloorSelectItem,
  toFloor,
  toFloorSelectItem,
} from 'src/components/FloorSelect/FloorSelect';
import { Section } from 'src/components/Section/Section';
import {
  toTown,
  toTownSelectItem,
  TownSelect,
  TownSelectItem,
} from 'src/components/TownSelect/TownSelect';
import { useHost } from 'src/providers/HostProvider';
import { getParkingState } from 'src/store/parking/selectors';
import { parkingActions } from 'src/store/parking/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { noop } from 'src/utils/noop';

import { ParkingBookingDateField } from '../ParkingBookingSettings/ParkingBookingDateField';
import { ParkingBookingTimeField } from '../ParkingBookingSettings/ParkingBookingTimeField';
import { ParkingSearch } from '../ParkingSearch/ParkingSearch';

import classes from './ParkingMapSettingsBottomSheet.module.scss';

const filterTownOptions = (option: TownSelectItem): boolean =>
  !!option.value.hasParkings;

const filterBuildingOptions = (option: BuildingSelectItem): boolean =>
  !!option.value.hasParkings;

const filterFloorOptions = (option: FloorSelectItem): boolean =>
  !!option.value.isParking;

const Inner: FC = () => {
  const dispatch = useAppDispatch();

  const handleChangeTown = useCallback(
    (value: TownSelectItem | undefined) => {
      dispatch(parkingActions.setTown(toTown(value)));
      dispatch(parkingActions.setBuilding(undefined));
      dispatch(parkingActions.setFloor(undefined));
    },
    [dispatch],
  );

  const handleChangeBuilding = useCallback(
    (value: BuildingSelectItem | undefined) => {
      dispatch(parkingActions.setBuilding(toBuilding(value)));
      dispatch(parkingActions.setFloor(undefined));
    },
    [dispatch],
  );

  const handleChangeFloor = useCallback(
    (value: FloorSelectItem | undefined) => {
      dispatch(parkingActions.setFloor(toFloor(value)));
    },
    [dispatch],
  );

  const { town, building, floor } = useSelector(getParkingState);

  const townSelectItem = useMemo(() => toTownSelectItem(town), [town]);
  const buildingSelectItem = useMemo(
    () => toBuildingSelectItem(building),
    [building],
  );
  const floorSelectItem = useMemo(() => toFloorSelectItem(floor), [floor]);

  return (
    <BottomSheet
      isOpen
      handleSetOpen={noop}
      swiper
      modal={false}
      snapPoints={[70, 0.5, 1]}
      style={{ minHeight: '70px' }}
      className={classes.settings}
      initialSnapPoint={0}
      header={{
        icon: Icons.Settings,
        title: 'Параметры',
      }}
    >
      <div className={classes.container}>
        <Section title='Дата и время'>
          <ParkingBookingDateField />
          <ParkingBookingTimeField />
        </Section>

        <Section title='Местоположение'>
          <TownSelect
            town={townSelectItem}
            onChange={handleChangeTown}
            filterOptions={filterTownOptions}
          />
          <BuildingSelect
            townId={town?.townId}
            building={buildingSelectItem}
            onChange={handleChangeBuilding}
            filterOptions={filterBuildingOptions}
            key={town?.townId}
          />
          <FloorSelect
            buildingId={building?.buildingId}
            floor={floorSelectItem}
            onChange={handleChangeFloor}
            filterOptions={filterFloorOptions}
            key={building?.buildingId}
          />
        </Section>

        <Section title='Поиск по номеру'>
          <ParkingSearch />
        </Section>
      </div>
    </BottomSheet>
  );
};

export const ParkingMapSettingsBottomSheet: FC = () => {
  const { host } = useHost();

  return host ? createPortal(<Inner />, host) : null;
};
